import { getLocale } from '@sonnen/web-utils';
import clsx from 'clsx';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAppContext } from '../AppContext/AppContext';
import { AuthGuard } from '../components/AuthGuard/AuthGuard';
import { DataSafeguard } from '../components/DataSafeguard/DataSafeguard';
import Footer from '../components/Footer/Footer';
import { Logout } from '../components/Logout/Logout';
import MobileCta from '../components/MobileCta/MobileCta';
import { SonnenHomeEnergyLogo } from '../components/SonnenHomeEnergyLogo/SonnenHomeEnergyLogo';
import { allRoutes, AppRoute, ROUTE_FLOW } from '../constants';
import { useCurrentRoute } from '../hooks/useCurrentRoute';
import { useCurrentUserData } from '../hooks/useCurrentUserData';
import { useOrderFlowStateMismatch } from '../hooks/useOrderFlowStateMismatch';
import { PageBackground } from '../types';

// Raw import functions — calling these prefetches the chunk without rendering
const prefetchMap: Partial<Record<AppRoute, () => Promise<unknown>>> = {
  [AppRoute.BATTERY_OPTIMIZATION]: () => import('../Pages/BatteryOptimization/BatteryOptimization'),
  [AppRoute.ORDER_COMPLETE]: () => import('../Pages/Confirmation/Confirmation'),
  [AppRoute.DATA_ENTRY]: () => import('../Pages/DataEntry/DataEntry'),
  [AppRoute.DATA_REVIEW]: () => import('../Pages/DataReview/DataReview'),
  [AppRoute.DIRECT_MARKETING]: () => import('../Pages/DirectMarketing/DirectMarketing'),
  [AppRoute.ELIGIBILITY_CHECK]: () => import('../Pages/EligibilityCheck/EligibilityCheck'),
  [AppRoute.ENERGY_STORE]: () => import('../Pages/EnergyStore/EnergyStore'),
  [AppRoute.MODULES_STORE]: () => import('../Pages/ModulesStore/ModulesStore'),
  [AppRoute.SELF_CONSUMPTION_PLUS]: () =>
    import('../Pages/SelfConsumptionPlus/SelfConsumptionPlus'),
  [AppRoute.TARIFF_OVERVIEW]: () => import('../Pages/TariffOverview/TariffOverview'),
  [AppRoute.TARIFFS_COMPARISON]: () => import('../Pages/TariffsComparison/TariffsComparison'),
};

const BatteryOptimization = lazy(() =>
  import('../Pages/BatteryOptimization/BatteryOptimization').then((m) => ({
    default: m.BatteryOptimization,
  })),
);
const Confirmation = lazy(() =>
  import('../Pages/Confirmation/Confirmation').then((m) => ({ default: m.Confirmation })),
);
const DataEntry = lazy(() => import('../Pages/DataEntry/DataEntry'));
const DataReview = lazy(() => import('../Pages/DataReview/DataReview'));
const DirectMarketing = lazy(() => import('../Pages/DirectMarketing/DirectMarketing'));
const EligibilityCheck = lazy(() => import('../Pages/EligibilityCheck/EligibilityCheck'));
const EnergyStore = lazy(() => import('../Pages/EnergyStore/EnergyStore'));
const Home = lazy(() => import('../Pages/Home/Home'));
const Login = lazy(() => import('../Pages/Login/Login').then((m) => ({ default: m.Login })));
const MaintenanceNotice = lazy(() => import('../Pages/MaintenanceNotice/MaintenanceNotice'));
const ModulesStore = lazy(() => import('../Pages/ModulesStore/ModulesStore'));
const PageNotFound = lazy(() => import('../Pages/PageNotFound/PageNotFound'));
const SelfConsumptionPlus = lazy(() =>
  import('../Pages/SelfConsumptionPlus/SelfConsumptionPlus').then((m) => ({
    default: m.SelfConsumptionPlus,
  })),
);
const TariffOverview = lazy(() => import('../Pages/TariffOverview/TariffOverview'));
const TariffsComparison = lazy(() => import('../Pages/TariffsComparison/TariffsComparison'));
import { buildPathWithPersistedSiteId } from '../utils/utils';
import styles from './AppRoutes.module.css';
import TopbarWithNameAndLogout from './TopbarWithNameAndLogout/TopbarWithNameAndLogout';

interface AuthProtectedRoute {
  path: AppRoute;
  element: React.ReactElement;
  allowVisitor: boolean;
}

// Route protection configuration
export const authProtectedRoutes: AuthProtectedRoute[] = [
  { path: AppRoute.SELF_CONSUMPTION_PLUS, element: <SelfConsumptionPlus />, allowVisitor: false },
  { path: AppRoute.TARIFFS_COMPARISON, element: <TariffsComparison />, allowVisitor: true },
  { path: AppRoute.TARIFF_OVERVIEW, element: <TariffOverview />, allowVisitor: true },
  { path: AppRoute.BATTERY_OPTIMIZATION, element: <BatteryOptimization />, allowVisitor: false },
  { path: AppRoute.ENERGY_STORE, element: <EnergyStore />, allowVisitor: false },
  { path: AppRoute.ELIGIBILITY_CHECK, element: <EligibilityCheck />, allowVisitor: false },
  { path: AppRoute.DIRECT_MARKETING, element: <DirectMarketing />, allowVisitor: false },
  { path: AppRoute.MODULES_STORE, element: <ModulesStore />, allowVisitor: false },
];

const renderDatasafe = (element: React.ReactNode) => {
  return <DataSafeguard>{element}</DataSafeguard>;
};

const stylePerBackground: Record<PageBackground, string> = {
  [PageBackground.HOME]: styles.homePageBackground,
  [PageBackground.CONFIRMATION]: styles.confirmationPageBackground,
  [PageBackground.CONTRACT_CREATION_BLOCKED]: styles.contractCreationBlockedBackground,
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export const AppRoutes = () => {
  const [userData, userDataLoading] = useCurrentUserData();
  const currentRoute = useCurrentRoute();
  const location = useLocation();
  const { currentBackground, orderFlow } = useAppContext();
  const locale = getLocale();
  useOrderFlowStateMismatch();

  useEffect(() => {
    // Persist incoming siteId early so redirects can recover it even if query gets dropped.
    buildPathWithPersistedSiteId(currentRoute, location.search);
  }, [currentRoute, location.search]);

  useEffect(() => {
    if (!orderFlow) return;
    ROUTE_FLOW[orderFlow].forEach((route) => prefetchMap[route]?.());
  }, [orderFlow]);

  useEffect(() => {
    const keyboardFixRoutes = new Set<AppRoute>([
      AppRoute.ELIGIBILITY_CHECK,
      AppRoute.DATA_ENTRY,
      AppRoute.DIRECT_MARKETING,
    ]);

    if (!keyboardFixRoutes.has(currentRoute)) return;
    if (!/Android/i.test(window.navigator.userAgent)) return;
    if (!window.visualViewport) return;

    let focusedElement: HTMLElement | null = null;
    let frameId: number | null = null;

    const isEditableField = (element: EventTarget | null): element is HTMLElement => {
      if (!(element instanceof HTMLElement)) return false;
      const tagName = element.tagName;
      return (
        tagName === 'INPUT' ||
        tagName === 'TEXTAREA' ||
        tagName === 'SELECT' ||
        element.isContentEditable
      );
    };

    const adjustKeyboardGap = () => {
      if (!focusedElement || !window.visualViewport) return;

      const keyboardHeight = window.innerHeight - window.visualViewport.height;
      if (keyboardHeight < 120) return;

      const rect = focusedElement.getBoundingClientRect();
      const desiredBottom = window.visualViewport.height - 16;
      const delta = rect.bottom - desiredBottom;

      if (Math.abs(delta) > 8) {
        window.scrollBy({ top: delta, behavior: 'auto' });
      }
    };

    const scheduleAdjust = () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(adjustKeyboardGap);
    };

    const onFocusIn = (event: FocusEvent) => {
      if (!isEditableField(event.target)) return;
      focusedElement = event.target;
      window.setTimeout(scheduleAdjust, 60);
    };

    const onFocusOut = (event: FocusEvent) => {
      if (focusedElement === event.target) {
        focusedElement = null;
      }
    };

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    window.visualViewport.addEventListener('resize', scheduleAdjust);
    window.visualViewport.addEventListener('scroll', scheduleAdjust);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      window.visualViewport?.removeEventListener('resize', scheduleAdjust);
      window.visualViewport?.removeEventListener('scroll', scheduleAdjust);
    };
  }, [currentRoute]);

  const routesWithLogo: AppRoute[] = allRoutes.filter(
    (route) =>
      route !== AppRoute.ROOT &&
      route !== AppRoute.LOGIN &&
      route !== AppRoute.MAINTENANCE &&
      route !== AppRoute.ORDER_COMPLETE,
  );
  const visibleSonnenHomeLogo = routesWithLogo.includes(currentRoute);

  const backgroundStyles = useMemo(() => {
    if (!currentBackground || !locale) return '';
    const localeStyle = styles[locale.substring(0, 2)];
    return clsx(stylePerBackground[currentBackground], localeStyle);
  }, [currentBackground, locale]);

  const authRoutes = useMemo(
    () =>
      authProtectedRoutes.map(({ path, element, allowVisitor }) => (
        <Route
          key={path}
          path={path}
          element={<AuthGuard allowVisitor={allowVisitor}>{element}</AuthGuard>}
        />
      )),
    [],
  );

  const getRootComponent = () => {
    if (userDataLoading) return <Login />;
    if (userData) {
      if (userData.featureFlags?.allowToAccessEnergyStore) {
        return <EnergyStore />;
      } else {
        return <Navigate to={AppRoute.TARIFFS_COMPARISON} />;
      }
    }
    return <Home />;
  };

  return (
    <div data-testid="app-routes" className={clsx(styles.appRoutes, backgroundStyles)}>
      <ScrollToTop />
      <TopbarWithNameAndLogout />

      {visibleSonnenHomeLogo && <SonnenHomeEnergyLogo />}

      <main className={styles.mainContent}>
        <Suspense fallback={null}>
          <Routes>
            <Route path={AppRoute.ROOT} element={getRootComponent()} />
            <Route path={AppRoute.MAINTENANCE} element={<MaintenanceNotice />} />
            <Route path={AppRoute.LOGIN} element={<Login />} />
            <Route path={AppRoute.LOGOUT} element={<Logout />} />
            {authRoutes}
            <Route path={AppRoute.DATA_ENTRY} element={renderDatasafe(<DataEntry />)} />
            <Route path={AppRoute.DATA_REVIEW} element={renderDatasafe(<DataReview />)} />
            <Route path={AppRoute.ORDER_COMPLETE} element={renderDatasafe(<Confirmation />)} />

            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />

      <MobileCta />
    </div>
  );
};
